/**
 * Graficas.
 *
 * Se dibujan con SVG a mano en lugar de cargar una libreria de graficacion. Son
 * dos formas —barras por dia y barras por hora— y una libreria costaria mas
 * kilobytes que toda la aplicacion, sobre el WiFi de una tienda.
 *
 * Reglas de lectura que se respetan aqui:
 *   - El eje empieza en cero. Recortarlo exagera diferencias y hace ver una
 *     buena semana como una catastrofe.
 *   - Se etiqueta el maximo, no todos los valores: el detalle exacto se
 *     consulta tocando la barra.
 */

import { useState, type ReactNode } from 'react';
import { formatMoneyShort } from '@abarrotes/shared';
import { cx } from './ui.js';

export interface BarPoint {
  label: string;
  value: number;
  /** Texto completo que se muestra al tocar la barra. */
  detail: string;
  highlight?: boolean;
}

export function BarChart({
  points,
  height = 160,
  className,
  emptyLabel = 'Sin datos todavia',
  formatMax = formatMoneyShort,
}: {
  points: BarPoint[];
  height?: number;
  className?: string;
  emptyLabel?: string;
  /**
   * Como se escribe el maximo del eje. Por omision, dinero.
   *
   * No todas las barras son pesos: la grafica de "a que hora entro la gente"
   * cuenta TICKETS, y con el formato de dinero su maximo —29 tickets— se
   * imprimia como "Maximo $0". Un rotulo que dice cero encima de barras que se
   * ven es peor que no ponerlo: parece que la grafica esta rota.
   */
  formatMax?: (value: number) => string;
}): ReactNode {
  const [active, setActive] = useState<number | null>(null);

  const max = Math.max(...points.map((p) => p.value), 0);

  if (points.length === 0 || max <= 0) {
    return (
      <div
        className={cx('grid place-items-center text-[13px] text-subtle', className)}
        style={{ height }}
      >
        {emptyLabel}
      </div>
    );
  }

  // Sin interaccion se describe el ultimo punto CON dato, no el ultimo del eje.
  // En la grafica por hora, el ultimo del eje son las 23:00, y arrancar
  // anunciando "23:00 — $0.00" parece que la grafica esta rota.
  const lastWithData = points.reduce(
    (best, point, index) => (point.value > 0 ? index : best),
    points.length - 1,
  );
  const shown = active ?? lastWithData;
  const current = points[shown];

  return (
    <div className={className}>
      <div className="flex items-baseline justify-between mb-3 gap-3">
        <span className="tabular text-[13px] text-muted truncate">{current?.detail ?? ''}</span>
        <span className="text-[12px] text-subtle shrink-0">Maximo {formatMax(max)}</span>
      </div>

      <div className="flex items-end gap-[3px]" style={{ height }} onPointerLeave={() => setActive(null)}>
        {points.map((point, index) => {
          // Altura minima visible: una barra de un pixel no se distingue de
          // "no hay dato", y son cosas distintas.
          const ratio = point.value / max;
          const pct = point.value > 0 ? Math.max(ratio * 100, 2) : 0;
          const isActive = index === shown;

          return (
            <button
              key={`${point.label}-${index}`}
              type="button"
              onPointerEnter={() => setActive(index)}
              onFocus={() => setActive(index)}
              onClick={() => setActive(index)}
              aria-label={point.detail}
              className="group relative flex-1 h-full flex items-end min-w-0"
            >
              <span
                className={cx(
                  'w-full rounded-t-[3px] transition-[height,background-color] duration-300',
                  point.highlight || isActive ? 'bg-primary' : 'bg-primary/35',
                  'group-hover:bg-primary',
                )}
                style={{ height: `${pct}%` }}
              />
            </button>
          );
        })}
      </div>

      <div className="flex justify-between mt-2 text-[11px] text-subtle">
        <span>{points[0]?.label}</span>
        <span>{points[points.length - 1]?.label}</span>
      </div>
    </div>
  );
}

/**
 * Barra de proporciones. Para desgloses donde importa el reparto y no la
 * evolucion: metodos de pago, categorias de gasto.
 */
export function ShareBar({
  segments,
}: {
  segments: { label: string; value: number; color: string }[];
}): ReactNode {
  const total = segments.reduce((acc, s) => acc + s.value, 0);
  if (total <= 0) {
    return <p className="text-[13px] text-subtle">Sin movimientos.</p>;
  }

  return (
    <div className="space-y-3">
      <div className="flex h-2.5 rounded-full overflow-hidden bg-surface-2">
        {segments.map((segment) => (
          <span
            key={segment.label}
            className="transition-[width] duration-500"
            style={{ width: `${(segment.value / total) * 100}%`, backgroundColor: segment.color }}
            title={segment.label}
          />
        ))}
      </div>

      <ul className="space-y-1.5">
        {segments.map((segment) => (
          <li key={segment.label} className="flex items-center gap-2 text-[13px]">
            <span
              className="size-2.5 rounded-full shrink-0"
              style={{ backgroundColor: segment.color }}
            />
            <span className="text-muted flex-1 truncate">{segment.label}</span>
            <span className="tabular text-ink font-medium">{formatMoneyShort(segment.value)}</span>
            <span className="tabular text-subtle w-10 text-right">
              {Math.round((segment.value / total) * 100)}%
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
